import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  collection, onSnapshot, query, orderBy, doc, getDocs, setDoc, deleteField,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { EXAM_CONFIG } from '../utils/examSubjects';
import AppShell from '../components/layout/AppShell';
import { getAppNavItems } from '../utils/appNavItems';
import { hasAllExamsUnlocked } from '../utils/examAccess';
import {
  activeRoundScores,
  cumulativeFromScores,
  listReadyExams,
  questionsForExam,
} from '../utils/questionBank';

export default function ExamsPage() {
  const { currentUser, isAdmin, hasSubscription } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [modules, setModules] = useState([]);
  const [daysByModule, setDaysByModule] = useState({});
  const [completedLessons, setCompletedLessons] = useState({});
  const [attemptDoc, setAttemptDoc] = useState(null);
  const [practiceExams, setPracticeExams] = useState([]);
  const [bankIdsBySubject, setBankIdsBySubject] = useState({});
  const [erCount, setErCount] = useState(0);
  const [imrCount, setImrCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const navItems = getAppNavItems(isAdmin, location.pathname, { hasSubscription });
  const finalizeRef = useRef(false);

  useEffect(() => {
    const dayUnsubs = {};
    const unsub = onSnapshot(
      query(collection(db, 'modules'), orderBy('order', 'asc')),
      (snap) => {
        const mods = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setModules(mods);
        setLoading(false);
        mods.forEach((mod) => {
          if (!dayUnsubs[mod.id]) {
            dayUnsubs[mod.id] = onSnapshot(
              query(collection(db, 'modules', mod.id, 'days'), orderBy('order', 'asc')),
              (dSnap) => {
                setDaysByModule((prev) => ({
                  ...prev,
                  [mod.id]: dSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
                }));
              }
            );
          }
        });
      }
    );
    return () => { unsub(); Object.values(dayUnsubs).forEach((u) => u()); };
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    return onSnapshot(doc(db, 'userProgress', currentUser.uid), (snap) => {
      setCompletedLessons(snap.exists() ? snap.data().completedLessons || {} : {});
    });
  }, [currentUser]);

  useEffect(() => {
    return onSnapshot(collection(db, 'practiceExams'), (snap) => {
      setPracticeExams(snap.docs.map((item) => ({ id: item.id, ...item.data() })));
    }, () => setPracticeExams([]));
  }, []);

  const examSubjectsKey = [...new Set(practiceExams.map((exam) => exam.subject).filter(Boolean))].sort().join('|');

  useEffect(() => {
    const subjects = examSubjectsKey ? examSubjectsKey.split('|') : [];
    if (!subjects.length) return undefined;
    let cancelled = false;
    Promise.all(subjects.map(async (subjectId) => {
      const snap = await getDocs(collection(db, 'questionBanks', subjectId, 'questions'));
      return [subjectId, snap.docs.map((item) => item.id)];
    })).then((rows) => {
      if (cancelled) return;
      setBankIdsBySubject(Object.fromEntries(rows));
    }).catch(() => {
      if (!cancelled) setBankIdsBySubject({});
    });
    return () => { cancelled = true; };
  }, [examSubjectsKey]);

  const banksPending = practiceExams.some((exam) => (
    exam.subject && !Object.prototype.hasOwnProperty.call(bankIdsBySubject, exam.subject)
  ));
  const readyRows = banksPending ? [] : listReadyExams(practiceExams, bankIdsBySubject);
  const activeScores = activeRoundScores(attemptDoc);

  useEffect(() => {
    if (!currentUser || isAdmin || finalizeRef.current || banksPending || !readyRows.length || !attemptDoc) return;
    const report = cumulativeFromScores(readyRows, activeScores);
    if (!report) return;
    if (attemptDoc.roundCleared && !attemptDoc.activeScores && !attemptDoc.bestScores) return;
    finalizeRef.current = true;
    setDoc(doc(db, 'examAttempts', currentUser.uid), {
      lastReport: { ...report, completedAt: attemptDoc.lastReport?.completedAt || report.completedAt },
      activeScores: deleteField(),
      activeAttempts: deleteField(),
      bestScores: deleteField(),
      attempts: deleteField(),
      roundCleared: true,
    }, { merge: true }).catch(() => {
      finalizeRef.current = false;
    });
  }, [currentUser, isAdmin, banksPending, readyRows, activeScores, attemptDoc]);

  useEffect(() => {
    if (!currentUser) return;
    return onSnapshot(doc(db, 'examAttempts', currentUser.uid), (snap) => {
      setAttemptDoc(snap.exists() ? snap.data() : {});
    });
  }, [currentUser]);

  useEffect(() => {
    return onSnapshot(
      collection(db, 'extendedResponses'),
      (snap) => {
        const n = snap.docs.filter((d) => {
          const subject = d.data()?.subject;
          return !subject || subject === 'language-arts';
        }).length;
        setErCount(n);
      },
      () => setErCount(0)
    );
  }, []);

  useEffect(() => {
    return onSnapshot(
      collection(db, 'imageMapReadings'),
      (snap) => {
        const n = snap.docs.filter((d) => {
          const subject = d.data()?.subject;
          return !subject || subject === 'social-studies';
        }).length;
        setImrCount(n);
      },
      () => setImrCount(0)
    );
  }, []);

  function isSubjectUnlocked(subjectId) {
    if (isAdmin || hasAllExamsUnlocked(currentUser?.email)) return true;
    const mods = modules.filter((m) => m.subject === subjectId);
    if (mods.length === 0) return true;
    return mods.every((mod) => {
      const days = daysByModule[mod.id] || [];
      if (days.length === 0) return true;
      return days.every((day) => completedLessons[day.id]);
    });
  }

  function subjectProgress(subjectId) {
    const mods = modules.filter((m) => m.subject === subjectId);
    const total = mods.reduce((s, m) => s + (daysByModule[m.id]?.length || 0), 0);
    const done = mods.reduce(
      (s, m) => s + (daysByModule[m.id] || []).filter((d) => completedLessons[d.id]).length,
      0
    );
    return { total, done };
  }

  const totalAll = Object.values(daysByModule).reduce((s, d) => s + (d?.length || 0), 0);
  const doneAll = Object.keys(completedLessons).length;
  const overallPct = totalAll > 0 ? Math.round((doneAll / totalAll) * 100) : 0;

  const lastReport = attemptDoc?.lastReport || null;

  return (
    <AppShell
      navItems={navItems}
      showBack
      backLabel="Dashboard"
      backPath="/dashboard"
    >
      <div style={s.wrap}>
        <div style={s.pageHeader}>
          <div style={s.headerIcon}>
            <span className="material-symbols-outlined" style={{ fontSize: 36, color: '#6332ed' }}>quiz</span>
          </div>
          <div>
            <h1 style={s.pageTitle}>GED Practice Exams</h1>
            <p style={s.pageSub}>
              Complete all lectures in a subject to unlock its practice exams.
              Each exam uses a unique set of questions from that subject’s bank. Pass with 70% or higher.
            </p>
          </div>
        </div>

        {loading ? (
          <p style={s.pageSub}>Loading exams…</p>
        ) : null}

        <div style={s.overallBar}>
          <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#6332ed' }}>trending_up</span>
          <span style={s.overallLabel}>Overall Course Progress</span>
          <div style={s.progressTrack}>
            <div style={{ ...s.progressFill, width: `${overallPct}%`, background: '#6332ed' }} />
          </div>
          <span style={s.overallPct}>{overallPct}%</span>
          <span style={s.overallCount}>{doneAll}/{totalAll} lectures</span>
        </div>

        <div className="sba-study-grid-2" style={s.grid}>
          {Object.entries(EXAM_CONFIG).map(([subjectId, cfg]) => {
            const unlocked = isSubjectUnlocked(subjectId);
            const { total, done } = subjectProgress(subjectId);
            const pct = total > 0 ? Math.round((done / total) * 100) : 0;
            const subjectExams = practiceExams
              .filter((exam) => exam.subject === subjectId)
              .sort((a, b) => (a.order || 0) - (b.order || 0));
            const roundRows = subjectExams
              .map((exam) => activeScores[exam.id])
              .filter(Boolean);
            const roundCorrect = roundRows.reduce((sum, score) => sum + (Number(score.correct) || 0), 0);
            const roundTotal = roundRows.reduce((sum, score) => sum + (Number(score.total) || 0), 0);
            const roundPct = roundTotal ? Math.round((roundCorrect / roundTotal) * 100) : 0;

            return (
              <div key={subjectId} style={s.card(cfg.color, unlocked)}>
                <div style={s.cardTopRow}>
                  <div style={{ ...s.cardIcon, background: cfg.color + '18' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 30, color: cfg.color }}>{cfg.icon}</span>
                  </div>
                  <div style={s.statusBadge(unlocked, false)}>
                    <span className="material-symbols-outlined" style={{ fontSize: 13 }}>
                      {!unlocked ? 'lock' : 'lock_open'}
                    </span>
                    {!unlocked ? 'Locked' : 'Unlocked'}
                  </div>
                </div>

                <div>
                  <h3 style={s.cardTitle}>{cfg.label}</h3>
                  <p style={s.cardDesc}>{cfg.description}</p>
                  {subjectId === 'language-arts' && erCount > 0 ? (
                    <p style={{ ...s.cardDesc, marginTop: 8, fontSize: 13, color: '#b45309' }}>
                      After lectures, practice {erCount} Extended Response{erCount === 1 ? '' : 's'} in the course (GED 3-trait scoring). This does not block the practice exam.
                    </p>
                  ) : null}
                  {subjectId === 'social-studies' && imrCount > 0 ? (
                    <p style={{ ...s.cardDesc, marginTop: 8, fontSize: 13, color: '#b45309' }}>
                      After lectures, practice {imrCount} Images and map Reading{imrCount === 1 ? '' : 's'} in the course (MCQs on the visual image). This does not block the practice exam.
                    </p>
                  ) : null}
                </div>

                <div style={s.metaRow}>
                  <span style={s.chip}><span className="material-symbols-outlined" style={{ fontSize: 13 }}>quiz</span>{subjectExams.length} exam{subjectExams.length === 1 ? '' : 's'}</span>
                  <span style={s.chip}><span className="material-symbols-outlined" style={{ fontSize: 13 }}>grade</span>70% pass</span>
                </div>

                <div style={s.lectureProgressWrap}>
                  <div style={s.lectureProgressHeader}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8' }}>Lectures completed</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: cfg.color }}>{done}/{total}</span>
                  </div>
                  <div style={s.progressTrack}>
                    <div style={{ ...s.progressFill, width: `${pct}%`, background: cfg.color }} />
                  </div>
                </div>

                {roundTotal > 0 && (
                  <div style={s.bestScore(roundPct >= 70)}>
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>history_edu</span>
                    This round: {roundCorrect}/{roundTotal} ({roundPct}%)
                  </div>
                )}

                {!unlocked ? (
                  <button disabled style={s.examBtn(cfg.color, false)}>
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>lock</span>
                    Complete all {total} lectures to unlock
                  </button>
                ) : subjectExams.length === 0 ? (
                  <p style={s.cardDesc}>No exams have been published for this subject yet.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {subjectExams.map((exam) => {
                      const examBest = activeScores[exam.id];
                      const lockedExam = Boolean(examBest) && !isAdmin;
                      const bankKnown = Object.prototype.hasOwnProperty.call(bankIdsBySubject, subjectId);
                      const assigned = questionsForExam(exam, subjectExams, bankIdsBySubject[subjectId] || []);
                      const ready = lockedExam || (bankKnown ? assigned.length > 0 : Number(exam.questionCount) > 0);
                      return (
                        <button
                          key={exam.id}
                          onClick={() => ready && navigate(`/exam/${subjectId}/${exam.id}`)}
                          disabled={!ready}
                          style={s.examBtn(cfg.color, ready)}
                        >
                          <span style={{ textAlign: 'left', flex: 1 }}>
                            <span style={{ display: 'block' }}>{exam.title || 'Practice exam'}</span>
                            <span style={{ display: 'block', fontSize: 11, fontWeight: 600, opacity: 0.85 }}>
                              {lockedExam
                                ? `Locked · ${examBest.percentage}% · Review report`
                                : `${exam.timeMinutes || 60} min · ${assigned.length || exam.questionCount || 0} questions`}
                            </span>
                          </span>
                          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                            {lockedExam ? 'lock' : ready ? 'play_circle' : 'hourglass_empty'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {lastReport && (
          <div style={s.reportCard(lastReport.ready)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 28, color: lastReport.ready ? '#15803d' : '#6332ed' }}>
                {lastReport.ready ? 'verified' : 'assessment'}
              </span>
              <h2 style={s.reportTitle}>Cumulative exam report</h2>
            </div>
            <p style={s.reportScore}>
              {lastReport.correct}/{lastReport.total} correct · {lastReport.percentage}%
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '16px 0' }}>
              {(lastReport.exams || []).map((row) => {
                const cfg = EXAM_CONFIG[row.subject] || {};
                return (
                  <div key={row.id} style={s.reportRow}>
                    <span style={{ fontWeight: 700, color: cfg.color || '#6332ed' }}>{cfg.label || row.subject}</span>
                    <span style={{ color: '#334155' }}>{row.title}</span>
                    <span style={{ marginLeft: 'auto', fontWeight: 800, color: row.percentage >= 70 ? '#15803d' : '#b45309' }}>
                      {row.correct}/{row.total} · {row.percentage}%
                    </span>
                  </div>
                );
              })}
            </div>
            {lastReport.ready ? (
              <div style={s.readyBanner}>
                <span className="material-symbols-outlined" style={{ fontSize: 22 }}>check_circle</span>
                <span>You are ready to appear in the exam. Contact the Advisor. All exams are open again.</span>
              </div>
            ) : (
              <div style={s.practiceBanner}>
                <span className="material-symbols-outlined" style={{ fontSize: 22 }}>school</span>
                <span>You need more practice. All exams are open again.</span>
              </div>
            )}
          </div>
        )}

        <div style={s.infoBox}>
          <span className="material-symbols-outlined" style={{ fontSize: 20, color: '#6332ed', flexShrink: 0 }}>info</span>
          <div>
            <p style={s.infoTitle}>About GED Practice Exams</p>
            <p style={s.infoText}>
              Each submitted exam stays locked for review until every published exam is finished. The cumulative report then appears. Under 70% you need more practice, and at 70% or higher you are ready to contact the Advisor. Either way, every exam opens again for another round.
            </p>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

const s = {
  wrap: { maxWidth: 900, margin: '0 auto', padding: '0 4px 80px', boxSizing: 'border-box' },
  pageHeader: { display: 'flex', alignItems: 'flex-start', gap: 18, marginBottom: 28 },
  headerIcon: { width: 64, height: 64, borderRadius: 18, background: 'rgba(75,0,209,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  pageTitle: { fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 26, fontWeight: 800, color: '#1b1b1d', letterSpacing: '-0.025em', marginBottom: 8 },
  pageSub: { fontSize: 14, color: '#64748b', lineHeight: 1.6, maxWidth: 500 },
  overallBar: { display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: '14px 18px', marginBottom: 28, flexWrap: 'wrap' },
  overallLabel: { fontSize: 13, fontWeight: 600, color: '#334155', whiteSpace: 'nowrap' },
  progressTrack: { flex: 1, height: 7, background: '#e2e8f0', borderRadius: 9999, overflow: 'hidden', minWidth: 60 },
  progressFill: { height: '100%', borderRadius: 9999, transition: 'width 0.4s' },
  overallPct: { fontSize: 13, fontWeight: 700, color: '#6332ed', flexShrink: 0 },
  overallCount: { fontSize: 12, color: '#94a3b8', flexShrink: 0 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20, marginBottom: 32 },
  card: (color, unlocked) => ({
    background: '#fff',
    border: `1.5px solid ${unlocked ? color + '40' : '#e2e8f0'}`,
    borderRadius: 18,
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    opacity: unlocked ? 1 : 0.7,
    boxShadow: unlocked ? `0 4px 24px ${color}10` : 'none',
  }),
  cardTopRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  cardIcon: { width: 54, height: 54, borderRadius: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  statusBadge: (unlocked, passed) => ({
    display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700,
    borderRadius: 20, padding: '4px 11px',
    background: !unlocked ? '#f1f5f9' : passed ? '#f0fdf4' : 'rgba(75,0,209,0.08)',
    color: !unlocked ? '#94a3b8' : passed ? '#15803d' : '#6332ed',
    border: `1px solid ${!unlocked ? '#e2e8f0' : passed ? '#bbf7d0' : 'rgba(75,0,209,0.2)'}`,
  }),
  cardTitle: { fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 15, fontWeight: 800, color: '#1b1b1d', letterSpacing: '-0.01em', marginBottom: 4 },
  cardDesc: { fontSize: 12, color: '#64748b', lineHeight: 1.5, margin: 0 },
  metaRow: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  chip: { display: 'flex', alignItems: 'center', gap: 4, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 20, padding: '3px 9px', fontSize: 11, fontWeight: 600, color: '#64748b' },
  lectureProgressWrap: { display: 'flex', flexDirection: 'column', gap: 6 },
  lectureProgressHeader: { display: 'flex', justifyContent: 'space-between' },
  bestScore: (passed) => ({
    display: 'flex', alignItems: 'center', gap: 6,
    background: passed ? '#f0fdf4' : '#fff7ed',
    color: passed ? '#15803d' : '#b45309',
    borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 600,
    border: `1px solid ${passed ? '#bbf7d0' : '#fed7aa'}`,
  }),
  reportCard: (ready) => ({
    background: ready ? '#f0fdf4' : '#fff',
    border: `1.5px solid ${ready ? '#86efac' : '#e2e8f0'}`,
    borderRadius: 18,
    padding: '22px 24px',
    marginBottom: 24,
  }),
  reportTitle: { fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 18, fontWeight: 800, color: '#1b1b1d', margin: 0 },
  reportScore: { fontSize: 22, fontWeight: 800, color: '#1b1b1d', margin: '4px 0 0' },
  reportRow: { display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 13, flexWrap: 'wrap' },
  readyBanner: {
    display: 'flex', alignItems: 'center', gap: 10,
    background: '#16a34a', color: '#fff', borderRadius: 12, padding: '14px 16px',
    fontSize: 15, fontWeight: 800, lineHeight: 1.4,
  },
  practiceBanner: {
    display: 'flex', alignItems: 'center', gap: 10,
    background: '#b45309', color: '#fff', borderRadius: 12, padding: '14px 16px',
    fontSize: 15, fontWeight: 800, lineHeight: 1.4,
  },
  reportNote: { fontSize: 13, color: '#64748b', margin: 0, lineHeight: 1.5 },
  examBtn: (color, unlocked) => ({
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    background: unlocked ? color : '#f1f5f9',
    color: unlocked ? '#fff' : '#94a3b8',
    border: 'none', borderRadius: 12, padding: '13px 18px',
    fontSize: 13, fontWeight: 700,
    cursor: unlocked ? 'pointer' : 'not-allowed',
    width: '100%', marginTop: 'auto',
    boxShadow: unlocked ? `0 4px 14px ${color}35` : 'none',
  }),
  infoBox: { display: 'flex', alignItems: 'flex-start', gap: 14, background: 'rgba(75,0,209,0.08)', border: '1.5px solid rgba(75,0,209,0.2)', borderRadius: 14, padding: '18px 20px' },
  infoTitle: { fontSize: 13, fontWeight: 700, color: '#4338ca', marginBottom: 4 },
  infoText: { fontSize: 13, color: '#4338ca', lineHeight: 1.6, margin: 0 },
};
